import { useCallback, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { markWorkspaceActivity } from "../api/client";
import { useUserSession } from "../context/UserContext";
import { useWorkspace } from "../context/WorkspaceContext";

const ACTIVITY_INTERVAL_MS = 15 * 60 * 1000;
const STORAGE_KEY_PREFIX = "workspaceActivityReportedAt";
const EXCLUDED_PATH_PREFIXES = [
  "/administration",
  "/order/",
  "/shopify/",
  "/login",
  "/logout",
  "/signup",
  "/oauth",
  "/load_workspace",
  "/reset_password",
  "/maintenance",
];

export const WorkspaceActivityReporter = () => {
  const { pathname } = useLocation();
  const { userSession, authReady } = useUserSession();
  const { activeWorkspace } = useWorkspace();

  const shouldReport =
    authReady &&
    Boolean(userSession?.token.accessToken && activeWorkspace?.workspaceId) &&
    pathname !== "/order" &&
    !EXCLUDED_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix));

  const reportActivity = useCallback(async () => {
    const token = userSession?.token.accessToken;
    const workspaceId = activeWorkspace?.workspaceId;
    if (
      !shouldReport ||
      !token ||
      !workspaceId ||
      document.visibilityState === "hidden"
    ) {
      return;
    }

    const storageKey = `${STORAGE_KEY_PREFIX}:${workspaceId}`;
    const previousValue = window.localStorage.getItem(storageKey);
    const previousTimestamp = Number(previousValue);
    const now = Date.now();

    if (
      Number.isFinite(previousTimestamp) &&
      now - previousTimestamp < ACTIVITY_INTERVAL_MS
    ) {
      return;
    }

    // Reserve the interval before the request so React StrictMode and multiple
    // tabs do not send duplicate heartbeats. Roll it back if the request fails.
    window.localStorage.setItem(storageKey, String(now));
    try {
      await markWorkspaceActivity(token, workspaceId);
    } catch {
      if (window.localStorage.getItem(storageKey) === String(now)) {
        if (previousValue === null) {
          window.localStorage.removeItem(storageKey);
        } else {
          window.localStorage.setItem(storageKey, previousValue);
        }
      }
    }
  }, [
    activeWorkspace?.workspaceId,
    shouldReport,
    userSession?.token.accessToken,
  ]);

  useEffect(() => {
    if (!shouldReport) return;

    void reportActivity();
    const intervalId = window.setInterval(() => {
      void reportActivity();
    }, ACTIVITY_INTERVAL_MS);
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        void reportActivity();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [pathname, reportActivity, shouldReport]);

  return null;
};
